import axios from 'axios'

function normalizeApiBaseUrl(rawBaseUrl) {
  // Local dev: CRA proxy uses "/api" relative path.
  if (!rawBaseUrl) return '/api'

  // Remove trailing slashes so we can safely append.
  const trimmed = rawBaseUrl.replace(/\/+$/, '')

  // Backend routes are under "/api/..." (see Django `survey_management/urls.py`).
  if (trimmed.endsWith('/api')) return trimmed
  return `${trimmed}/api`
}

// IMPORTANT (Heroku):
// Browsers may block cross-site cookies between two different *.herokuapp.com apps.
// To make login sessions reliable, we prefer same-origin API calls in production and
// let the frontend server proxy `/api/*` to the Django backend.
//
// - Local dev: CRA proxy (package.json "proxy") already makes `/api` work.
// - Production: our Node server (`server.js`) proxies `/api` to the backend.
//
// So in the browser we always use relative `/api`. We keep normalizeApiBaseUrl
// for any non-browser usage / future flexibility.
const API_BASE_URL =
  typeof window !== 'undefined' ? '/api' : normalizeApiBaseUrl(process.env.REACT_APP_API_URL)

// Create axios instance with default config
// In production, use full backend URL; in local dev, use relative URL with proxy
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for Django session cookies
})

// Request interceptor to add CSRF token
api.interceptors.request.use(
  (config) => {
    // Get CSRF token from cookie
    const csrfToken = getCookie('csrftoken')
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Handle 204 No Content responses (common for DELETE requests)
    if (response.status === 204) {
      // Return a success response object even for 204
      return { ...response, data: { status: 'success', message: 'Deleted successfully' } }
    }
    return response
  },
  (error) => {
    // Don't redirect during login flow - let the login handler manage it
    const isLoginFlow = error.config?.url?.includes('/auth/login') || 
                        error.config?.url?.includes('/auth/user')
    
    if ((error.response?.status === 401 || error.response?.status === 403) && !isLoginFlow) {
      // Redirect to login on unauthorized or forbidden
      // 403 can occur when session expires or user doesn't have permission
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Helper function to get cookie value
function getCookie(name) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

// Auth API
export const authAPI = {
  getCsrfToken: () => api.get('/auth/csrf-token/'),
  login: (username, password) => api.post('/auth/login/', { username, password }),
  logout: () => api.post('/auth/logout/'),
  getCurrentUser: () => api.get('/auth/user/'),
  changePassword: (data) => api.post('/auth/change-password/', data),
}

// Survey API
export const surveyAPI = {
  list: (params) => api.get('/surveys/', { params }),
  get: (id) => api.get(`/surveys/${id}/`),
  create: (data) => api.post('/surveys/', data),
  update: (id, data) => api.put(`/surveys/${id}/`, data),
  delete: (id) => api.delete(`/surveys/${id}/`),
  toggleStatus: (id) => api.post(`/surveys/${id}/toggle-status/`),
  toggleInvoiceSent: (id, invoiceSent) => api.post(`/surveys/${id}/toggle-invoice-sent/`, { invoice_sent: invoiceSent }),
  export: (params) => api.get('/surveys/export/', { params, responseType: 'blob' }),
  generateReference: () => api.get('/surveys/generate-reference/'),
  getInvoiceData: (id) => api.get(`/surveys/${id}/invoice-data/`),
  saveInvoice: (id, data) => api.patch(`/surveys/${id}/invoice-save/`, data),
}

// Operation API
export const operationAPI = {
  list: () => api.get('/operations/'),
  get: (id) => api.get(`/operations/${id}/`),
  create: (data) => api.post('/operations/', data),
  update: (id, data) => api.put(`/operations/${id}/`, data),
  delete: (id) => api.delete(`/operations/${id}/`),
  createFromSurvey: (surveyRef) => api.post('/operations/create-from-survey/', { survey_ref: surveyRef }),
  deleteByRef: (referenceNumber) => api.post(`/operations/${referenceNumber}/delete/`),
  getDashboardData: (params) => api.get('/operations/dashboard-data/', { params }),
  save: (data) => api.post('/operations/save/', data),
}

// Dashboard API
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats/', { params }),
  generateSOA: (params) => api.get('/dashboard/generate-soa/', { params }),
  exportSOA: (params) => api.get('/dashboard/export-soa/', { params, responseType: 'blob' }),
}

// Reference Data API
export const referenceAPI = {
  getPorts: (country) => api.get('/ports/', { params: { country } }),
  getSurveyTypes: () => api.get('/survey-types/'),
  getSurveyorsByType: (type) => {
    const params = type ? { type } : {}
    return api.get('/surveyors/by-type/', { params })
  },
  getCountries: () => api.get('/countries/'),
  getClients: () => api.get('/clients/'),
}

// Admin API
export const adminAPI = {
  // Users
  getUsers: () => api.get('/admin/users/'),
  createUser: (data) => api.post('/admin/users/', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}/`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}/`),
  // Surveyors
  getSurveyors: () => api.get('/admin/surveyors/'),
  createSurveyor: (data) => api.post('/admin/surveyors/', data),
  updateSurveyor: (id, data) => api.put(`/admin/surveyors/${id}/`, data),
  deleteSurveyor: (id) => api.delete(`/admin/surveyors/${id}/`),
  // Clients
  getClients: () => api.get('/admin/clients/'),
  createClient: (data) => api.post('/admin/clients/', data),
  updateClient: (id, data) => api.put(`/admin/clients/${id}/`, data),
  deleteClient: (id) => api.delete(`/admin/clients/${id}/`),
  // Countries
  getCountries: () => api.get('/admin/countries/'),
  createCountry: (data) => api.post('/admin/countries/', data),
  updateCountry: (id, data) => api.put(`/admin/countries/${id}/`, data),
  deleteCountry: (id) => api.delete(`/admin/countries/${id}/`),
  // Ports
  getPorts: () => api.get('/admin/ports/'),
  createPort: (data) => api.post('/admin/ports/', data),
  updatePort: (id, data) => api.put(`/admin/ports/${id}/`, data),
  deletePort: (id) => api.delete(`/admin/ports/${id}/`),
  // Survey Types
  getSurveyTypes: () => api.get('/admin/survey-types/'),
  createSurveyType: (data) => api.post('/admin/survey-types/', data),
  updateSurveyType: (id, data) => api.put(`/admin/survey-types/${id}/`, data),
  deleteSurveyType: (id) => api.delete(`/admin/survey-types/${id}/`),
  // Operations
  getOperations: () => api.get('/admin/operations/'),
  createOperation: (data) => api.post('/admin/operations/', data),
  updateOperation: (id, data) => api.put(`/admin/operations/${id}/`, data),
  deleteOperation: (id) => api.delete(`/admin/operations/${id}/`),
  // Events
  getEvents: () => api.get('/admin/events/'),
  createEvent: (data) => api.post('/admin/events/', data),
  updateEvent: (id, data) => api.put(`/admin/events/${id}/`, data),
  deleteEvent: (id) => api.delete(`/admin/events/${id}/`),
}

// Survey Reports API - files attached to one specific survey (by reference number).
// Access mirrors survey access; download always goes through the authenticated
// download endpoint below, never a raw storage URL.
export const reportsAPI = {
  list: (referenceNumber) => api.get(`/surveys/${referenceNumber}/reports/`),
  upload: (referenceNumber, formData) => api.post(`/surveys/${referenceNumber}/reports/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (referenceNumber, reportId) => api.delete(`/surveys/${referenceNumber}/reports/${reportId}/`),
}

// Feedback API (To Developer)
export const feedbackAPI = {
  getMyThread: () => api.get('/feedback/my-thread/'),
  startNewThread: () => api.post('/feedback/start-new/'),
  postMessage: (threadId, body) => api.post(`/feedback/threads/${threadId}/messages/`, { body }),
  updateStatus: (threadId, statusValue) => api.patch(`/feedback/threads/${threadId}/status/`, { status: statusValue }),
  getThreadDetail: (threadId) => api.get(`/feedback/threads/${threadId}/`),
  deleteThread: (threadId) => api.delete(`/feedback/threads/${threadId}/`),
  getInbox: () => api.get('/feedback/inbox/'),
  getUserOptions: () => api.get('/feedback/user-options/'),
  startThreadForUser: (userId, body) => api.post('/feedback/start-for-user/', { user_id: userId, body }),
}

export default api

